import {PageSectionContainer} from '../elements/index';
import {twMerge} from 'tailwind-merge';
import {IconFooterFacebook} from '../icons/IconFooterFacebook';
import {IconFooterInstagram} from '../icons/IconFooterInstagram';
import {IconFooterTwitter} from '../icons/IconFooterTwitter';
import {IconFooterYoutube} from '../icons/IconFooterYoutube';
import {FooterNewsletter} from './FooterNewsletter.client';
import {FooterEntity} from '~/types';
import {FooterItemsEntity} from '~/types/FooterEntity';
import clsx from 'clsx';
import {DATA_TEST_IDS} from '~/lib/const';

export function Footer({data}: {data?: FooterEntity}) {
  if (!data) {
    return null;
  }

  const columnCount = data.items?.length ?? 0;
  // If there is only 1 column, render it on the second position for layout to work correctly.
  // Because first column goes under the static Company column
  const firstColumn = columnCount > 1 ? data.items?.[0] : null;
  const secondColumn = columnCount > 1 ? data.items?.[1] : data.items?.[0];
  return (
    <footer className="bg-black text-white pt-[60px] xl:pt-[93px] pb-[50px] xl:pb-[44px] flex flex-col mt-[30px] xl:mt-[45px]">
      <PageSectionContainer className="my-0 md:my-0">
        <div className="flex flex-col xl:flex-row">
          <FooterNewsletter />

          <div className="mt-[33px] xl:mt-0 flex flex-col md:flex-row space-y-[37px] md:space-y-0">
            <div
              className={clsx(
                'flex md:flex-row 2xl:flex-row',
                firstColumn == null
                  ? 'flex-col xl:flex-col'
                  : 'flex-col-reverse xl:flex-col-reverse',
              )}
            >
              <div className="flex flex-col xl:ml-[78px]">
                <span className="font-header font-bold text-sm tracking-[0.07rem]">
                  COMPANY
                </span>
                {data.address && (
                  <span className="mt-[13px] md:mt-4 text-white md:text-gray-400 leading-7 w-[131px]">
                    {data.address}
                  </span>
                )}
                {data.phone && (
                  <a
                    data-testid={DATA_TEST_IDS.LINK('phone')}
                    href={`tel:${data.phone}`}
                    className="mt-6 text-white md:text-gray-400 hover:underline"
                  >
                    {data.phone}
                  </a>
                )}
                {data.email && (
                  <a
                    data-testid={DATA_TEST_IDS.LINK('email')}
                    href={`mailto:${data.email}`}
                    className="mt-6 text-white md:text-gray-400 hover:underline"
                  >
                    {data.email}
                  </a>
                )}
              </div>

              {firstColumn && <FooterItems items={firstColumn} index={0} />}
            </div>

            <FooterItems items={secondColumn} index={1} />
          </div>
        </div>

        <div className="border-t border-t-gray-300 mt-[56px] md:mt-[49px] flex flex-col pt-[32px]">
          <SocialIcons data={data} className="xl:hidden mb-9 justify-center" />

          <div className="flex justify-between items-center">
            <span className="xl:w-64 text-14/16">
              © {new Date().getFullYear()} SkiEssentials
            </span>

            <SocialIcons data={data} className="hidden xl:flex" />

            <div className="space-x-5 xl:space-x-10 xl:w-64 justify-end flex text-white md:text-gray-400 text-14/16">
              {data.termsLink && (
                <a
                  data-testid={DATA_TEST_IDS.LINK('terms')}
                  href={data.termsLink}
                  className="hover:underline"
                >
                  Terms & Conditions
                </a>
              )}
              {data.siteMapLink && (
                <a
                  data-testid={DATA_TEST_IDS.LINK('site-map')}
                  href={data.siteMapLink}
                  className="hover:underline"
                >
                  Site Map
                </a>
              )}
            </div>
          </div>
        </div>
      </PageSectionContainer>
    </footer>
  );
}

function FooterItems(props: {items?: FooterItemsEntity; index: number}) {
  if (!props.items || !props.items.items || props.items.items.length === 0) {
    return null;
  }

  return (
    <div
      className={clsx({
        'flex flex-col md:ml-[150px] lg:ml-[197px] xl:ml-[78px] 2xl:ml-[197px] 3xl:ml-[197px] mb-[55px] md:mb-0 xl:mb-[55px] 2xl:mb-0':
          props.index === 0,
        'md:ml-[150px] xl:ml-[291px]': props.index === 1,
      })}
    >
      {props.items.items.map((item, i) => (
        <div
          key={item.name}
          className={clsx('flex flex-col', {
            ' mt-[37px]': i > 0,
          })}
        >
          <span className="font-header font-bold text-sm tracking-[0.07rem]">
            {item.name}
          </span>
          <span className="mt-[15px] leading-[29px] text-white md:text-gray-400 flex flex-col">
            {item.items?.map((item) => (
              <a key={item.title} href={item.link} className="hover:underline">
                {item.title}
              </a>
            ))}
          </span>
        </div>
      ))}
    </div>
  );
}

function SocialIcons({
  className,
  data,
}: {
  className?: string;
  data: FooterEntity;
}) {
  return (
    <div className={twMerge('flex space-x-[60px] items-center', className)}>
      {data.youtubeLink && (
        <a href={data.youtubeLink} aria-label="Youtube">
          <IconFooterYoutube className="text-white md:text-gray-400 md:hover:text-white" />
        </a>
      )}
      {data.facebookLink && (
        <a href={data.facebookLink} aria-label="Facebook">
          <IconFooterFacebook className="text-white md:text-gray-400 md:hover:text-white" />
        </a>
      )}
      {data.instagramLink && (
        <a href={data.instagramLink} aria-label="Instagram">
          <IconFooterInstagram className="text-white md:text-gray-400 md:hover:text-white" />
        </a>
      )}
      {data.twitterLink && (
        <a href={data.twitterLink} aria-label="Twitter">
          <IconFooterTwitter className="text-white md:text-gray-400 md:hover:text-white" />
        </a>
      )}
    </div>
  );
}
